import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { AuthImg } from "../../components/AuthImg";
import { useConfirm } from "../../components/ConfirmProvider";
import { EquipmentTransferDialog } from "../../components/EquipmentTransfer";
import { Plus, Package, Trash2, Mail, Download, UserX, ArrowDownToLine } from "lucide-react";
import { toast } from "sonner";
import { fmtRoDate } from "../../lib/dateFmt";

// ---------- Assigned Items Dialog ----------
function AssignedItemsDialog({ person, onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canSendEmail = ["admin", "magazioner", "planificator"].includes(user?.role);
  const canAllocate = ["admin", "magazioner"].includes(user?.role);
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [toEmail, setToEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [allocateOpen, setAllocateOpen] = useState(false);

  const reload = () => {
    api.get(`/persons/${person.id}/assigned-items`)
      .then((r) => setData(r.data))
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Eroare la încărcare");
        setData({ items: [], counts: { total: 0, echipament: 0, psa: 0 } });
      });
  };

  useEffect(() => {
    reload();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person.id]);

  const unassignItem = async (it) => {
    try {
      // Folosim transferul "pickup" — magazionerul/admin-ul preia echipamentul înapoi de la persoană.
      await api.post(`/inventory/transfers`, {
        action: "pickup",
        item_id: it.item_id,
        counterparty_person_id: person.id,
        note: "Returnat din profilul angajatului",
      });
      toast.success("Echipament returnat");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { data: resp } = await api.post(`/persons/${person.id}/assigned-items/pdf`);
      if (!resp.pdf_file_id) throw new Error("PDF indisponibil");
      const blob = await api.get(`/files/${resp.pdf_file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(blob.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("PDF generat");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la PDF");
    } finally {
      setDownloading(false);
    }
  };

  const sendEmail = async () => {
    if (!toEmail || !toEmail.includes("@")) {
      toast.warning("Introdu o adresă de email validă");
      return;
    }
    setSending(true);
    try {
      await api.post(`/persons/${person.id}/assigned-items/email`, { to_email: toEmail, message: msg });
      toast.success(`Email trimis către ${toEmail}`);
      setEmailOpen(false);
      setMsg("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la trimitere email");
    } finally {
      setSending(false);
    }
  };

  const counts = data?.counts || { total: 0, echipament: 0, psa: 0 };
  const items = data?.items || [];

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="assigned-items-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-700">
            <Package size={18}/> Echipament alocat · {person.nume_full || `${person.prenume || ""} ${person.nume || ""}`.trim()}
          </DialogTitle>
        </DialogHeader>

        {!data ? (
          <div className="p-6 text-sm text-zinc-400">Se încarcă echipamentele…</div>
        ) : (
          <>
            {/* Totals */}
            <div className="grid grid-cols-3 gap-2 mt-2">
              <div className="card-flat p-3 text-center">
                <div className="small-label">Total</div>
                <div className="font-display text-2xl font-extrabold text-zinc-800">{counts.total}</div>
              </div>
              <div className="card-flat p-3 text-center">
                <div className="small-label">Echipamente / scule</div>
                <div className="font-display text-2xl font-extrabold text-zinc-700">{counts.echipament}</div>
              </div>
              <div className="card-flat p-3 text-center bg-cyan-50 border-cyan-200">
                <div className="small-label text-cyan-800">PSA</div>
                <div className="font-display text-2xl font-extrabold text-cyan-800">{counts.psa}</div>
              </div>
            </div>

            {items.length === 0 ? (
              <div className="mt-4 p-8 text-center text-zinc-400 italic border border-dashed rounded-md" data-testid="assigned-items-empty">
                Niciun echipament sau articol PSA alocat în prezent.
              </div>
            ) : (
              <div className="mt-4 space-y-2" data-testid="assigned-items-list">
                {items.map((it) => (
                  <div key={it.item_id} className="card-flat p-3 flex items-start gap-3 hover:border-blue-200 transition-colors" data-testid={`assigned-item-${it.item_id}`}>
                    <div className="w-14 h-14 flex-shrink-0 rounded-md overflow-hidden bg-zinc-100 flex items-center justify-center">
                      {it.photo_id ? (
                        <AuthImg fileId={it.photo_id} className="w-full h-full object-cover"/>
                      ) : (
                        <Package size={22} className="text-zinc-400"/>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {it.item_type === "psa" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800 font-bold">PSA</span>}
                        <span className="font-semibold truncate">{it.item_name || "—"}</span>
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-1 space-y-0.5">
                        <div>📅 Predat pe <b>{String(it.handover_ts || "").slice(0, 10) || "—"}</b>{it.handover_by ? <> · de <b>{it.handover_by}</b></> : null}</div>
                        <div>📍 Locație curentă: <b>{it.location || "—"}</b></div>
                        {it.note && <div className="italic">📝 {it.note}</div>}
                      </div>
                    </div>
                    {canAllocate && (
                      <button
                        type="button"
                        onClick={() => unassignItem(it)}
                        className="text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded p-1.5 self-start"
                        title="Returnează în magazie"
                        data-testid={`unassign-item-${it.item_id}`}
                      >
                        <Trash2 size={14}/>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Email overlay */}
        {emailOpen && (
          <div className="mt-3 card-flat p-3 border-blue-200 bg-blue-50/40">
            <div className="text-xs font-bold text-blue-800 mb-2 flex items-center gap-1"><Mail size={14}/> Trimite lista pe email</div>
            <div className="space-y-2">
              <Input
                type="email"
                placeholder="angajat@example.com"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                className="h-9"
                data-testid="assigned-email-to"
              />
              <Textarea
                rows={2}
                placeholder="Mesaj opțional (va apărea în corpul emailului)"
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setEmailOpen(false)} disabled={sending}>Anulează</Button>
                <Button size="sm" onClick={sendEmail} disabled={sending} className="bg-blue-600 hover:bg-blue-700" data-testid="assigned-email-send">
                  {sending ? "Se trimite…" : "Trimite PDF pe email"}
                </Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>Închide</Button>
          {canAllocate && (
            <Button
              onClick={() => setAllocateOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="allocate-item-btn"
            >
              <Plus size={14} className="mr-1.5"/>
              Alocă echipament
            </Button>
          )}
          <Button
            variant="outline"
            onClick={downloadPdf}
            disabled={downloading || !data}
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="assigned-pdf-btn"
          >
            <Download size={14} className="mr-1.5"/>
            {downloading ? "Se generează…" : "Descarcă PDF"}
          </Button>
          {canSendEmail && (
            <Button
              onClick={() => setEmailOpen(!emailOpen)}
              disabled={!data || items.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="assigned-email-btn"
            >
              <Mail size={14} className="mr-1.5"/>
              Trimite pe email
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      {allocateOpen && (
        <AllocateItemDialog
          person={person}
          onClose={() => setAllocateOpen(false)}
          onAllocated={() => { setAllocateOpen(false); reload(); }}
        />
      )}
    </Dialog>
  );
}


// ---------- Allocate item to person dialog ----------
function AllocateItemDialog({ person, onClose, onAllocated }) {
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [picked, setPicked] = useState(null);
  const todayIso = new Date().toISOString().slice(0, 10);
  const [operationDate, setOperationDate] = useState(todayIso);

  useEffect(() => {
    api.get(`/inventory`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, []);

  const available = useMemo(() => {
    if (!items) return [];
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      // Doar echipamente / PSA, nu consumabile / piese / ulei
      if (!["echipament", "psa"].includes(it.item_type || "echipament")) return false;
      // Trebuie să fie funcționale (excludem defectele)
      if (it.status === "defect") return false;
      // Excludem cele deja alocate altcuiva
      if (it.assigned_to_person_id && it.assigned_to_person_id !== person.id) return false;
      // Excludem cele alocate deja persoanei curente
      if (it.assigned_to_person_id === person.id) return false;
      if (typeFilter !== "all" && (it.item_type || "echipament") !== typeFilter) return false;
      if (q) {
        const hay = `${it.name || ""} ${it.note || ""} ${it.location || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, typeFilter, person.id]);

  const allocate = async () => {
    if (!picked) return toast.warning("Selectează un echipament");
    setSubmitting(true);
    try {
      await api.post(`/inventory/transfers`, {
        action: "handover",
        item_id: picked.id,
        counterparty_person_id: person.id,
        note: note || `Alocat din profilul angajatului`,
        operation_date: operationDate || null,
      });
      toast.success(`„${picked.name}" alocat lui ${person.nume_full || person.prenume || ""}`);
      onAllocated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la alocare");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="allocate-item-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-700">
            <Plus size={18}/> Alocă echipament către {person.nume_full || `${person.prenume || ""} ${person.nume || ""}`.trim()}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          {/* Search + type filter */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-500">Caută denumire / locație / notă</Label>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="ex: bormașină, cască..."
                className="mt-1"
                data-testid="allocate-search"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Tip</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="mt-1" data-testid="allocate-type-filter"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate (echipament + PSA)</SelectItem>
                  <SelectItem value="echipament">Doar echipament</SelectItem>
                  <SelectItem value="psa">Doar PSA</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* List */}
          <div className="border rounded max-h-[40vh] overflow-y-auto" data-testid="allocate-items-list">
            {!items ? (
              <div className="p-6 text-sm text-zinc-400">Se încarcă…</div>
            ) : available.length === 0 ? (
              <div className="p-6 text-sm text-zinc-400 italic text-center">
                Niciun echipament disponibil. Verifică filtrele sau magazia.
              </div>
            ) : (
              available.map((it) => {
                const sel = picked?.id === it.id;
                return (
                  <button
                    type="button"
                    key={it.id}
                    onClick={() => setPicked(it)}
                    className={`w-full text-left p-3 flex items-start gap-3 border-b last:border-b-0 transition-colors ${sel ? "bg-emerald-50 border-l-4 border-l-emerald-500" : "hover:bg-zinc-50"}`}
                    data-testid={`allocate-item-row-${it.id}`}
                  >
                    <div className="w-12 h-12 flex-shrink-0 rounded-md overflow-hidden bg-zinc-100 flex items-center justify-center">
                      {it.photo_ids?.[0] ? <AuthImg fileId={it.photo_ids[0]} className="w-full h-full object-cover"/> : <Package size={20} className="text-zinc-400"/>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {it.item_type === "psa" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-800 font-bold">PSA</span>}
                        <span className="font-semibold truncate">{it.name || "—"}</span>
                        {it.quantity > 1 && <span className="text-[10px] text-zinc-500">{it.quantity} {it.unit || "buc"}</span>}
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">
                        📍 {it.location || "—"}
                        {it.color && <> · 🎨 {it.color}</>}
                        {it.note && <> · 📝 {it.note}</>}
                      </div>
                    </div>
                    {sel && <span className="text-emerald-600 text-xl leading-none">✓</span>}
                  </button>
                );
              })
            )}
          </div>

          {picked && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs text-zinc-500">Notă alocare (opțional)</Label>
                <Textarea
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="ex: Pentru proiectul X, returnabil la finalizare"
                  className="mt-1"
                  data-testid="allocate-note"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">📅 Data alocării</Label>
                <Input
                  type="date"
                  value={operationDate}
                  onChange={(e) => setOperationDate(e.target.value)}
                  max={todayIso}
                  className="mt-1"
                  data-testid="allocate-operation-date"
                />
                <div className="text-[10px] text-zinc-500 mt-0.5">Default azi</div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Anulează</Button>
          <Button
            onClick={allocate}
            disabled={!picked || submitting}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="allocate-confirm-btn"
          >
            <Plus size={14} className="mr-1.5"/>
            {submitting ? "Se alocă…" : picked ? `Alocă „${picked.name}"` : "Selectează echipamentul"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Dismissal Dialog ----------
function DismissalDialog({ person, onClose, onDone }) {
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [prices, setPrices] = useState({}); // item_id -> number
  const [selected, setSelected] = useState({}); // item_id -> bool
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);
  const [pickupOpen, setPickupOpen] = useState(false);

  const loadItems = () => {
    api.get(`/persons/${person.id}/dismissal-items`)
      .then((r) => {
        setData(r.data);
        const sel = {};
        (r.data?.items || []).forEach((it) => { if (it.still_assigned) sel[it.item_id] = true; });
        setSelected(sel);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Eroare la încărcare");
        setData({ items: [], person: { nume_full: "" } });
      });
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person.id]);

  const activeItems = useMemo(() => (data?.items || []).filter((i) => i.still_assigned), [data]);
  const returnedItems = useMemo(() => (data?.items || []).filter((i) => !i.still_assigned), [data]);

  const total = useMemo(() => {
    let t = 0;
    activeItems.forEach((it) => {
      if (!selected[it.item_id]) return;
      const v = parseFloat(prices[it.item_id] || 0);
      if (!isNaN(v) && v > 0) t += v;
    });
    return t;
  }, [activeItems, selected, prices]);

  const submit = async () => {
    const items = activeItems
      .filter((it) => selected[it.item_id])
      .map((it) => ({
        item_id: it.item_id,
        cost: parseFloat(prices[it.item_id] || 0) || 0,
        note: it.item_name,
      }));
    // Echipamentele cu cost 0 sunt trimise și ele — vor fi doar recuperate fizic, fără reținere salarială.
    const noEquipment = items.length === 0;
    const withCost = items.filter((x) => x.cost > 0).length;
    const confirmBody = noEquipment
      ? `Confirmi finalizarea concedierii pentru ${person.nume_full}?\n\nNu reținem nimic din salariu (angajatul nu are echipamente de predat).\nUltima zi de muncă: ${date}`
      : `Confirmi finalizarea concedierii pentru ${person.nume_full}?\n\nTotal de reținut: ${total.toFixed(2)} CHF\nEchipamente recuperate: ${items.length}${withCost < items.length ? ` (din care ${items.length - withCost} fără reținere)` : ""}\nUltima zi de muncă: ${date}\n\nEchipamentele vor fi returnate automat.`;
    if (!(await confirm({ body: confirmBody, danger: true }))) return;
    setSubmitting(true);
    try {
      const { data: resp } = await api.post(`/persons/${person.id}/finalize-dismissal`, {
        items,
        date,
        reason,
      });
      const msg = noEquipment
        ? `Concediere finalizată (fără reținere)`
        : `Concediere finalizată: ${resp.processed_items} echipamente · ${resp.total_deducted} CHF reținut`;
      toast.success(msg);
      // Open generated process-verbal PDF in a new tab, authenticated via token
      if (resp.pdf_file_id) {
        try {
          const blob = await api.get(`/files/${resp.pdf_file_id}`, { responseType: "blob" });
          const url = URL.createObjectURL(blob.data);
          window.open(url, "_blank");
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
          // non-blocking: pdf preview failed, but dismissal is complete
          console.error("PDF preview failed", e);
        }
      }
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la finalizare");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !submitting && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dismissal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <UserX size={18}/> Concediere · {person.nume_full || `${person.prenume || ""} ${person.nume || ""}`.trim()}
          </DialogTitle>
        </DialogHeader>

        {!data ? (
          <div className="p-6 text-sm text-zinc-400">Se încarcă echipamentele…</div>
        ) : (
          <>
            <div className="text-xs text-zinc-600 bg-amber-50 border border-amber-200 rounded-md p-3">
              ⚠️ Bifează echipamentele care nu au fost returnate și completează <strong>valoarea de reținut</strong> pentru fiecare.
              La finalizare, fiecare echipament este recuperat automat (PSA merge la SERGHEI ACASA), iar suma totală se adaugă ca reținere sub categoria <strong>CONCEDIERE</strong> în salariul angajatului.
            </div>

            <div className="grid grid-cols-2 gap-2 mt-3">
              <div>
                <Label className="small-label">Ultima zi de muncă</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9" data-testid="dismissal-last-day"/>
                <div className="text-[10px] text-zinc-400 mt-0.5">Va fi salvată ca „data concedierii" pe persoană.</div>
              </div>
              <div>
                <Label className="small-label">Motiv (opțional)</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: Demisie voluntară" className="h-9"/>
              </div>
            </div>

            {/* ACTIVE ITEMS (needs price) */}
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase font-bold text-zinc-600">
                  Echipamente în responsabilitatea angajatului ({activeItems.length})
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setPickupOpen(true)}
                  className="h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="dismiss-preia-btn"
                  title="Preia un echipament în avans (înainte de finalizarea concedierii)"
                >
                  <ArrowDownToLine size={12} className="mr-1"/> Preia echipament
                </Button>
              </div>
              {activeItems.length === 0 ? (
                <div className="p-4 text-center text-sm text-zinc-400 border border-dashed rounded-md">
                  Niciun echipament activ de recuperat.
                </div>
              ) : (
                <div className="border border-zinc-200 rounded-md divide-y max-h-80 overflow-y-auto">
                  {activeItems.map((it) => (
                    <div key={it.item_id} className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50" data-testid={`dismiss-row-${it.item_id}`}>
                      <input
                        type="checkbox"
                        checked={!!selected[it.item_id]}
                        onChange={(e) => setSelected((s) => ({ ...s, [it.item_id]: e.target.checked }))}
                        className="w-4 h-4"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold flex items-center gap-1.5">
                          {it.item_type === "psa" && <span className="text-[9px] px-1 py-0.5 rounded bg-cyan-100 text-cyan-800 font-bold">PSA</span>}
                          <span className="truncate">{it.item_name}</span>
                        </div>
                        <div className="text-[10px] text-zinc-500">
                          📅 Predat pe {String(it.handover_ts || "").slice(0, 10)}
                          {it.handover_by ? ` · de ${it.handover_by}` : ""}
                          {it.note ? ` · ${String(it.note).slice(0, 50)}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={prices[it.item_id] || ""}
                          onChange={(e) => setPrices((p) => ({ ...p, [it.item_id]: e.target.value }))}
                          className="w-24 h-8 text-xs"
                          disabled={!selected[it.item_id]}
                          data-testid={`dismiss-price-${it.item_id}`}
                        />
                        <span className="text-[10px] text-zinc-500">CHF</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* RETURNED (informative) */}
            {returnedItems.length > 0 && (
              <div className="mt-3">
                <details>
                  <summary className="text-[10px] uppercase font-bold text-zinc-500 cursor-pointer hover:text-zinc-700">
                    Deja returnate ({returnedItems.length}) — informativ
                  </summary>
                  <div className="mt-2 border border-zinc-200 rounded-md divide-y max-h-40 overflow-y-auto opacity-70">
                    {returnedItems.map((it) => (
                      <div key={it.item_id} className="flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-500">
                        <span className="text-[9px] bg-zinc-100 px-1 py-0.5 rounded font-bold">RETURNAT</span>
                        {it.item_type === "psa" && <span className="text-[9px] px-1 py-0.5 rounded bg-cyan-50 text-cyan-700">PSA</span>}
                        <span className="flex-1 truncate">{it.item_name}</span>
                        <span className="font-mono text-[10px]">Predat {String(it.handover_ts || "").slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}

            {/* TOTAL */}
            <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase font-bold text-red-700">Total de reținut</div>
                <div className="text-[10px] text-zinc-600">Se va adăuga în Salarii sub categoria CONCEDIERE</div>
              </div>
              <div className="font-display text-2xl font-extrabold text-red-700" data-testid="dismiss-total">
                {total.toFixed(2)} <span className="text-sm text-zinc-500">CHF</span>
              </div>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Anulează</Button>
          <Button
            onClick={submit}
            disabled={submitting || !data}
            className="bg-red-600 hover:bg-red-700"
            data-testid="dismiss-submit-btn"
          >
            <UserX size={14} className="mr-1.5"/>
            {submitting ? "Se finalizează…" : (total > 0 ? "Finalizează concedierea" : "Concediază (fără reținere)")}
          </Button>
        </DialogFooter>

        {pickupOpen && (
          <EquipmentTransferDialog
            action="pickup"
            presetSourcePerson={person}
            lockSource={true}
            onClose={() => setPickupOpen(false)}
            onDone={() => { setPickupOpen(false); loadItems(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}


// ---------- Rehire Dialog ----------
function RehireDialog({ person, onClose, onDone }) {
  const [rehireDate, setRehireDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [history, setHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!person?.id) return;
    api.get(`/persons/${person.id}/rehire-history`)
      .then((r) => setHistory(r.data?.rows || []))
      .catch(() => setHistory([]));
  }, [person?.id]);

  const submit = async () => {
    if (!rehireDate || rehireDate.length !== 10) {
      toast.error("Selectează o dată validă");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/persons/${person.id}/rehire`, { rehire_date: rehireDate });
      toast.success(`✅ ${person.nume_full || person.nume || "Persoană"} reangajată de la ${rehireDate}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la reangajare");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="rehire-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">🔄 Reangajează persoana</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-sm">
            <div className="text-zinc-500 text-xs uppercase tracking-wider font-semibold mb-1">Angajat</div>
            <div className="font-semibold text-zinc-800" data-testid="rehire-person-name">
              {person.nume_full || `${person.prenume || ""} ${person.nume || ""}`.trim()}
            </div>
          </div>

          <div className="text-xs bg-zinc-50 border border-zinc-200 rounded px-2 py-2 space-y-0.5">
            <div className="flex justify-between">
              <span className="text-zinc-500">Angajare anterioară:</span>
              <span className="font-mono">{person.data_angajarii || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Concediat pe:</span>
              <span className="font-mono text-red-700">{person.data_concedierii || "—"}</span>
            </div>
            {person.dismissal_reason && (
              <div className="text-zinc-600 italic mt-1 text-[11px]">
                „{person.dismissal_reason}"
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-700">📅 Reîncepe activitatea din</Label>
            <Input
              type="date"
              value={rehireDate}
              onChange={(e) => setRehireDate(e.target.value)}
              className="h-9 text-sm mt-1"
              data-testid="rehire-date-input"
            />
            <div className="text-[11px] text-zinc-500 mt-1">
              Persoana va fi <b>reactivată</b>, <b>data concedierii ștearsă</b> și data angajării setată la cea de mai sus.
            </div>
          </div>

          {history.length > 0 && (
            <div className="border-t border-zinc-200 pt-2" data-testid="rehire-history-block">
              <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1.5">
                🕒 Istoric reangajări ({history.length})
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1 text-xs">
                {history.slice(0, 10).map((h) => (
                  <div
                    key={h.id}
                    className="flex items-center gap-1.5 text-[11px] text-zinc-700 bg-zinc-50 rounded px-2 py-1"
                    data-testid={`rehire-history-row-${h.id}`}
                  >
                    <span className="font-mono text-zinc-500 flex-shrink-0">
                      {(h.created_at || "").slice(0, 10)}
                    </span>
                    <span className="text-zinc-400">·</span>
                    <span className="font-mono">
                      concediat {fmtRoDate(h.previous_dismissal_date) || "—"} → reangajat <b className="text-green-700">{fmtRoDate(h.new_hire_date)}</b>
                    </span>
                    <span className="text-zinc-400 ml-auto truncate text-[10px]">{h.user_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="rehire-cancel-btn">
            Anulează
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !rehireDate}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="rehire-confirm-btn"
          >
            {submitting ? "Se reangajează…" : "🔄 Confirmă reangajarea"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { AssignedItemsDialog, AllocateItemDialog, DismissalDialog, RehireDialog };
